import { useEffect, useState } from "react";
import { useAuth } from "../auth/auth-context";
import {
  connectGoogleCalendar,
  hasGoogleCalendarAccess,
  isGoogleCalendarLinked,
  reconnectGoogleCalendar,
} from "./google-auth";

// Garde au niveau module : le hook est monté par AppMenu sur TOUS les écrans —
// l'auto-reconnect (un redirect OAuth complet) ne doit être tenté qu'UNE fois
// par chargement d'app, pas à chaque navigation.
let autoReconnectTried = false;

/** Réservé aux tests : ré-arme la garde d'auto-reconnect. */
export function resetAutoReconnectForTests(): void {
  autoReconnectTried = false;
}

// Lien compte Google (calendrier) : état + actions, extrait de l'accueil pour
// être consommé par le menu global. Auto-reconnect si identité liée mais
// provider_token absent (cas typique après signOut/signIn) — UNE tentative
// par chargement d'app pour éviter une boucle si le reconnect échoue.
export function useGoogleCalendarLink() {
  const { session, client } = useAuth();
  const connected = hasGoogleCalendarAccess(session);
  const linked = isGoogleCalendarLinked(session);
  const [error, setError] = useState<string | null>(null);
  const [reconnecting, setReconnecting] = useState(false);

  useEffect(() => {
    if (!session || !linked || connected) return;
    if (autoReconnectTried) return;
    autoReconnectTried = true;
    setReconnecting(true);
    reconnectGoogleCalendar(client, session)
      .then((r) => {
        if (!r.ok) setError(r.error ?? "Reconnexion échouée");
      })
      .catch(() => setError("Reconnexion échouée"))
      .finally(() => setReconnecting(false));
  }, [session, linked, connected, client]);

  async function connect() {
    setError(null);
    // Si déjà liée mais provider_token absent → reconnect (unlink + link).
    // Sinon premier link.
    const r =
      linked && session
        ? await reconnectGoogleCalendar(client, session)
        : await connectGoogleCalendar(client);
    if (!r.ok) setError(r.error ?? "Connexion échouée");
  }

  return { connected, linked, reconnecting, error, connect };
}
